import React, { useState } from 'react';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import {
  Shield,
  Lock,
  Envelope
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Basic validation
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    if (!password) {
      setError('Please enter your password');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/api/auth/login', { 
        email: email, 
        password: password
      });
      
      // Check if user is admin
      if (response.data.user.role !== 'admin') {
        setError('Access denied. Admin privileges required.');
        setLoading(false);
        return;
      }
      
      // Store admin token and clear couple context
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userType', 'admin');
      localStorage.removeItem('weddingId');
      
      // Set token in API headers
      api.defaults.headers.common['x-auth-token'] = response.data.token;
      
      // Set user data directly from login response to avoid race conditions
      if (response.data.user) {
        setUser(response.data.user);
      }
      
      // Trigger auth context re-initialization as backup
      window.dispatchEvent(new CustomEvent('authChange'));
      
      setSuccess('Login successful! Redirecting to admin dashboard...');
      
      // Redirect to admin dashboard
      setTimeout(() => {
        navigate('/admin/dashboard', { replace: true });
      }, 1500);
      
    } catch (error) {
      setError(error.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Box display="flex" flexDirection="column" alignItems="center">
        {/* Header */}
        <Box display="flex" alignItems="center" mb={3}>
          <Shield size={48} weight="regular" color="#1976d2" />
          <Typography variant="h4" component="h1" sx={{ ml: 2, fontWeight: 'bold' }}>
            Admin Access
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" textAlign="center" mb={4}>
          Secure admin authentication required
        </Typography>

        <Card sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent sx={{ p: 4 }}>
            <form onSubmit={handleLogin}>
              <Box display="flex" alignItems="center" mb={3}>
                <Lock size={24} weight="regular" color="#666" />
                <Typography variant="h6" sx={{ ml: 1 }}>
                  Admin Login
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Admin Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter admin email address"
                sx={{ mb: 3 }}
                helperText="Enter the authorized admin email address"
                required
                InputProps={{
                  startAdornment: <Envelope size={20} style={{ marginRight: 8, color: '#666' }} />
                }}
              />

              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                sx={{ mb: 3 }}
                required
                InputProps={{
                  startAdornment: <Lock size={20} style={{ marginRight: 8, color: '#666' }} />
                }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Shield size={20} />}
                sx={{ py: 1.5 }}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Security Notice */}
        <Card sx={{ width: '100%', maxWidth: 400, mt: 3, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="body2" textAlign="center">
              <strong>Security Notice:</strong> This is a secure admin area. All access attempts are logged and monitored.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}

export default AdminLogin;